import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowLeft, Calendar, Clock, User, Share2, CheckCircle } from 'lucide-react';

const IncorporationBlogPost = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-[#1a1a1a]">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ 
              backgroundImage: 'url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&h=800&fit=crop)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/80 via-[#1a1a1a]/90 to-[#1a1a1a]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 pt-12">
          {/* Back Link */}
          <Link 
            to="/community" 
            className="inline-flex items-center text-white/60 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Community
          </Link>

          {/* Category */}
          <div className="mb-6">
            <span className="bg-[#8D6E63] text-white text-xs font-medium px-4 py-1.5 rounded-full">
              Incorporation
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            The Complete Checklist for US Company Incorporation
          </h1>

          {/* Subtitle */}
          <p className="text-xl text-white/70 mb-8 max-w-3xl">
            Everything you need to know before incorporating your company in the United States
          </p>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-white/60 text-sm">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              <span>Trueledger Team</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              <span>December 10, 2025</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              <span>10 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Lead Paragraph */}
        <div className="mb-12">
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light">
            Incorporating a business in the United States is a pivotal step that shapes your company's future—from taxation and liability protection to investor appeal and operational flexibility. This comprehensive checklist guides you through every critical decision and requirement.
          </p>
        </div>

        <div className="prose prose-lg max-w-none">
          {/* Section 1 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">1</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Choose Your Structure for Scale
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-6">
                Your legal structure dictates your taxation, ownership flexibility, and investor appeal. You must choose the vehicle that aligns with your long-term strategy:
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="bg-stone-50 border border-stone-200 p-5 rounded-lg">
                  <h4 className="font-bold text-[#3E2723] mb-2">LLC (Limited Liability Company)</h4>
                  <p className="text-gray-600 text-sm">
                    The king of flexibility and pass-through taxation, making it the go-to for many small businesses and lean startups.
                  </p>
                </div>
                <div className="bg-stone-50 border border-stone-200 p-5 rounded-lg">
                  <h4 className="font-bold text-[#3E2723] mb-2">C-Corporation</h4>
                  <p className="text-gray-600 text-sm">
                    The undisputed gold standard for venture-backed startups, prized for its ability to issue multiple classes of stock and seamlessly attract institutional capital.
                  </p>
                </div>
                <div className="bg-stone-50 border border-stone-200 p-5 rounded-lg">
                  <h4 className="font-bold text-[#3E2723] mb-2">S-Corporation</h4>
                  <p className="text-gray-600 text-sm">
                    Offers the benefit of pass-through taxation but comes with strict ownership restrictions and rigid eligibility requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">2</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Select Your State of Incorporation
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-6">
                You are not restricted to incorporating where you live or operate. Businesses can plant their flag in any US state, and the choice is highly strategic:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-[#3E2723] text-white p-5 rounded-lg text-center">
                  <h4 className="font-bold mb-2">Delaware</h4>
                  <p className="text-white/80 text-sm">
                    The default choice for high-growth startups due to its predictable corporate laws.
                  </p>
                </div>
                <div className="bg-[#5D4037] text-white p-5 rounded-lg text-center">
                  <h4 className="font-bold mb-2">Wyoming</h4>
                  <p className="text-white/80 text-sm">
                    A fortress for founders seeking strong privacy protections and low filing fees.
                  </p>
                </div>
                <div className="bg-[#8D6E63] text-white p-5 rounded-lg text-center">
                  <h4 className="font-bold mb-2">California / Texas</h4>
                  <p className="text-white/80 text-sm">
                    Often practical if your operations will be heavily concentrated there.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">3</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Claim Your Corporate Identity
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-4">
                Your company name must be completely unique within your chosen state of incorporation. It also must carry a legal identifier (such as Inc., LLC, or Corporation) and steer clear of restricted terms like "Bank" or "Insurance" unless you have secured special state approval.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 p-5 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-1">Name Requirements Checklist</h4>
                    <ul className="text-amber-700 text-sm space-y-1">
                      <li>• Unique within your state</li>
                      <li>• Includes legal identifier (Inc., LLC, Corp.)</li>
                      <li>• Avoids restricted terms without approval</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">4</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Appoint Your Registered Agent
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-4">
                Every US company requires a legal anchor in its state of incorporation. A registered agent is a designated entity or individual with a physical address in the state, available during normal business hours to receive critical legal notices, lawsuits, and government correspondence on your behalf.
              </p>
              
              <div className="bg-stone-50 border-l-4 border-[#3E2723] p-6">
                <p className="text-gray-800 font-medium">
                  Your registered agent serves as your company's official point of contact for all legal and government communications.
                </p>
              </div>
            </div>
          </div>

          {/* Section 5 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">5</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                File the Formation Documents
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <div className="bg-stone-50 border-l-4 border-[#8D6E63] p-6 mb-4">
                <p className="text-[#3E2723] font-semibold text-lg">
                  This is the moment your company officially exists.
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                You must file formation documents with your state—typically Articles of Organization for LLCs or Articles of Incorporation for C-Corps. These public documents lock in your company name, registered agent details, business address, and initial ownership structure.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-stone-50 p-4 rounded-lg text-center">
                  <p className="text-[#3E2723] font-bold">For LLCs</p>
                  <p className="text-gray-600 text-sm">Articles of Organization</p>
                </div>
                <div className="bg-stone-50 p-4 rounded-lg text-center">
                  <p className="text-[#3E2723] font-bold">For C-Corps</p>
                  <p className="text-gray-600 text-sm">Articles of Incorporation</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">6</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Obtain Your EIN (Employer Identification Number)
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-4">
                Issued directly by the IRS, your EIN acts as your company's corporate social security number. Even if you are a solo founder with zero employees, this number is an absolute prerequisite for opening a business bank account, processing payments, and filing taxes.
              </p>
              
              <div className="bg-[#3E2723] text-white p-6 rounded-lg">
                <h4 className="font-semibold text-[#8D6E63] mb-3">You need an EIN to:</h4>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-3 text-[#8D6E63]" />
                    <span>Open a business bank account</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-3 text-[#8D6E63]" />
                    <span>Process payments and payroll</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-3 text-[#8D6E63]" />
                    <span>File federal and state taxes</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section 7 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">7</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Establish Your Internal Governance
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <div className="bg-stone-50 border-l-4 border-[#3E2723] p-6 mb-6">
                <p className="text-gray-800 font-medium italic">
                  "A company without internal rules is a liability waiting to happen."
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">
                Immediately after formation, establish your operating framework to define ownership rights, voting power, and dispute resolution:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-stone-50 border border-stone-200 p-5 rounded-lg">
                  <h4 className="font-bold text-[#3E2723] mb-2">For LLCs</h4>
                  <p className="text-gray-600 text-sm">
                    Draft an ironclad Operating Agreement.
                  </p>
                </div>
                <div className="bg-stone-50 border border-stone-200 p-5 rounded-lg">
                  <h4 className="font-bold text-[#3E2723] mb-2">For Corporations</h4>
                  <p className="text-gray-600 text-sm">
                    Establish your corporate Bylaws and Shareholder Agreements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 8 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">8</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Build the Financial Firewall
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <div className="bg-stone-50 border-l-4 border-red-500 p-6 my-6">
                <p className="text-gray-800 font-bold text-lg italic">
                  "Never pierce the corporate veil."
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Opening a dedicated US business bank account is essential to maintain a strict legal separation between your personal wealth and your business finances. To open an account, banks will demand your finalized formation documents, EIN confirmation, and founder identification.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 p-5 rounded-lg">
                <h4 className="font-semibold text-amber-800 mb-2">Required Documents for Bank Account</h4>
                <ul className="text-amber-700 text-sm space-y-1">
                  <li>• Finalized formation documents</li>
                  <li>• EIN confirmation letter</li>
                  <li>• Founder identification</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section 9 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">9</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Map Your Tax Obligations
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <div className="bg-[#3E2723] text-white p-5 mb-6 rounded-lg">
                <p className="font-semibold">
                  Compliance is not optional.
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">
                The moment you incorporate, you trigger a cascade of tax regulations. Depending on your structure and footprint, you must build a roadmap for:
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-stone-50 p-4 rounded-lg flex items-center">
                  <CheckCircle className="w-5 h-5 text-[#3E2723] mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">Federal income tax filings</span>
                </div>
                <div className="bg-stone-50 p-4 rounded-lg flex items-center">
                  <CheckCircle className="w-5 h-5 text-[#3E2723] mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">State tax filings</span>
                </div>
                <div className="bg-stone-50 p-4 rounded-lg flex items-center">
                  <CheckCircle className="w-5 h-5 text-[#3E2723] mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">Sales tax registration</span>
                </div>
                <div className="bg-stone-50 p-4 rounded-lg flex items-center">
                  <CheckCircle className="w-5 h-5 text-[#3E2723] mr-3 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">Payroll taxes (if hiring)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section 10 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">10</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Implement Institutional-Grade Accounting
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-4">
                Do not wait until tax season to organize your books. Implement robust financial systems from the beginning. Select scalable accounting software, establish rigorous bookkeeping processes, and maintain perfect documentation for every expense and revenue dollar.
              </p>
              
              <div className="bg-gradient-to-r from-[#3E2723] to-[#5D4037] text-white p-8 rounded-lg">
                <p className="text-lg font-medium leading-relaxed">
                  Accurate, audit-ready financial records are the backbone of investor readiness and operational survival.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Box */}
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-8 mt-12">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Your Incorporation Checklist Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              "Choose your business structure",
              "Select your state of incorporation",
              "Claim your corporate identity",
              "Appoint your registered agent",
              "File formation documents",
              "Obtain your EIN",
              "Establish internal governance",
              "Build the financial firewall",
              "Map your tax obligations",
              "Implement institutional-grade accounting"
            ].map((item, index) => (
              <div key={index} className="flex items-center">
                <span className="w-6 h-6 bg-[#3E2723] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">
                  {index + 1}
                </span>
                <span className="text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Share & CTA Section */}
        <div className="mt-16 pt-12 border-t border-stone-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Share this article:</span>
              <button className="p-2 bg-stone-100 rounded-full hover:bg-stone-200 transition-colors">
                <Share2 className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <a href="mailto:hello@trueledger.com?subject=US%20Incorporation%20Inquiry&body=Hi%20Trueledger%20Team,%0A%0AI%20read%20your%20article%20on%20US%20company%20incorporation%20and%20would%20like%20to%20learn%20more%20about%20your%20services.%0A%0AThank%20you.">
              <button className="bg-[#3E2723] text-white px-8 py-3 rounded-lg hover:bg-[#5D4037] transition-colors">
                Start Your Incorporation Journey
              </button>
            </a>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default IncorporationBlogPost;
